// Relative imports (not `@/`) so bun can test this module.
import type { EntityId, SimState } from '../sim';

/**
 * Health-bar bookkeeping for HealthBarsLayer (pure, no React/Three): when
 * each unit and building was last in combat (attacking, attacked, shooting
 * or shot) and last hit, so a bar shows while it's damaged or fighting,
 * flashes on a hit and fades out once the fighting stops.
 */

/** Seconds after the last combat a bar stays up at full hp. */
export const COMBAT_LINGER = 4;
/** Seconds a full-hp bar takes to fade out after that. */
export const BAR_FADE = 0.6;
/** Seconds a hit flashes the bar. */
const HIT_FLASH = 0.25;

export interface CombatMark {
  /** Hp at the last tick seen. */
  hp: number;
  /** Clock time (s) of the last tick it was in combat; -Infinity when never. */
  combat: number;
  /** Clock time (s) of the last tick its hp dropped; -Infinity when never. */
  hit: number;
}

/**
 * Updates `marks` for one sim tick at clock time `now` (seconds, any clock
 * the caller then reads bars with): who fights this tick (`engaged`: attacker
 * → target, sim `engagements`; shots fired or landing hits since the last
 * tick), whose hp dropped. Entities gone are forgotten.
 */
export function trackCombat(
  marks: Map<EntityId, CombatMark>,
  state: SimState,
  engaged: ReadonlyMap<EntityId, EntityId>,
  now: number
): void {
  const active = new Set<EntityId>();
  for (const [attacker, target] of engaged) {
    active.add(attacker);
    active.add(target);
  }
  for (const shot of state.shots ?? []) {
    if (state.tick - shot.fireTick <= 1) active.add(shot.attacker);
    if (shot.hit && state.tick - shot.impactTick <= 1 && shot.impactTick <= state.tick)
      active.add(shot.targetId);
  }
  const seen = (id: EntityId, hp: number) => {
    const mark = marks.get(id);
    if (!mark) {
      marks.set(id, { hp, combat: active.has(id) ? now : -Infinity, hit: -Infinity });
      return;
    }
    const hit = hp < mark.hp - 1e-6;
    if (hit) mark.hit = now;
    if (hit || active.has(id)) mark.combat = now;
    mark.hp = hp;
  };
  for (const u of Object.values(state.units)) seen(u.id, u.hp);
  for (const b of Object.values(state.buildings)) seen(b.id, b.hp);
  for (const id of marks.keys()) {
    if (!state.units[id] && !state.buildings[id]) marks.delete(id);
  }
}

/** Whether a mark's last combat was recent enough for its bar to be up, or fading, at `now`. */
export function inCombat(mark: CombatMark | undefined, now: number): boolean {
  return !!mark && now - mark.combat <= COMBAT_LINGER;
}

/**
 * How much of the bar shows (0 hidden … 1 full): always while damaged
 * (`fraction` < 1), else while in combat, then fading out over BAR_FADE.
 */
export function barVisibility(mark: CombatMark | undefined, fraction: number, now: number): number {
  if (fraction < 1 - 1e-6) return 1;
  if (!mark) return 0;
  const since = now - mark.combat - COMBAT_LINGER;
  if (since <= 0) return 1;
  return since >= BAR_FADE ? 0 : 1 - since / BAR_FADE;
}

/** Whether the bar flashes for a hit at `now`. */
export function barFlash(mark: CombatMark | undefined, now: number): boolean {
  return !!mark && now - mark.hit <= HIT_FLASH;
}

/**
 * The fill's colour blend for an hp fraction: [from, to, t] over the stops
 * ok (full) → warn (half) → danger (empty).
 */
export function barColorStops(fraction: number): ['ok' | 'warn', 'warn' | 'danger', number] {
  const f = Math.min(1, Math.max(0, fraction));
  if (f >= 0.5) return ['ok', 'warn', (1 - f) / 0.5];
  return ['warn', 'danger', (0.5 - f) / 0.5];
}
